import Phaser from 'phaser';
import type { CombatPosition, EntityCombatStateDTO } from '@hexcrawl/shared-utils';
import { EntityStatus } from '@hexcrawl/shared-utils';
import { useCombatStore } from '@/ui/stores/combatStore';
import { abilityService } from '@/ui/services/abilityService';
import type { CombatCoordinates } from './CombatCoordinates';
import type { CombatContext } from './CombatContext';
import { DepthOrder } from './constants';

export class CombatHighlights {
  private _moveHighlights: Phaser.GameObjects.Shape[] = [];
  private _rangeHighlights: Phaser.GameObjects.Shape[] = [];
  private _targetHighlights: Phaser.GameObjects.Shape[] = [];
  private _hoverHighlight: Phaser.GameObjects.Shape | null = null;
  private _aoeRadiusHighlights: Phaser.GameObjects.Shape[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly coords: CombatCoordinates,
    private readonly tileWidth: number,
    private readonly tileHeight: number,
    private readonly context: CombatContext,
  ) {}

  upateHighlights() {
    this._moveHighlights.forEach(h => h.destroy());
    this._moveHighlights = [];
    this._rangeHighlights.forEach(h => h.destroy());
    this._rangeHighlights = [];
    this._targetHighlights.forEach(h => h.destroy());
    this._targetHighlights = [];

    const combatStore = useCombatStore.getState();

    if (combatStore.inputMode === 'target-selection') {
      // Range tiles: blue outline showing ability reach
      for (const pos of this.context.getAbilityRangeTiles()) {
        const highlight = this.createTileHighlight(pos.x, pos.y, 0x4488ff, 0.3, 0, 0);
        if (highlight) {
          highlight.setDepth(DepthOrder.RANGE_HIGHLIGHTS);
          this._rangeHighlights.push(highlight);
        }
      }

      const abilityId = useCombatStore.getState().selectedAbilityId;
      const selectedAbility = abilityId ? (abilityService.getAbilityFromCache(abilityId) ?? null) : null;
      if (!selectedAbility?.target.aoeRadius) {
        // Valid target tiles: orange fill on targetable entities (single-target abilities)
        for (const entity of this.context.combat.entityStates) {
          if (entity.status === EntityStatus.DEAD) continue;
          if (!this.context.isValidAbilityTarget(entity)) continue;
          const highlight = this.createTileHighlight(entity.position.x, entity.position.y, 0xff8800, 0.2, 2, 0.65);
          if (highlight) {
            highlight.setDepth(DepthOrder.TARGET_HIGHLIGHTS);
            this._targetHighlights.push(highlight);
          }
        }
      }
      return;
    }

    if (!this.context.canPlayerMove()) return;

    const validPositions = this.context.getValidMovePositions();
    for (const pos of validPositions) {
      const highlight = this.createTileHighlight(pos.x, pos.y, 0x00ff00, 0.2);
      if (highlight) {
        highlight.setDepth(DepthOrder.MOVE_HIGHLIGHTS);
        this._moveHighlights.push(highlight);
      }
    }
  }

  updateHoverHighlight(tilePos: CombatPosition | null, queueActive: boolean) {
    if (this._hoverHighlight) {
      this._hoverHighlight.destroy();
      this._hoverHighlight = null;
    }
    this._aoeRadiusHighlights.forEach(h => h.destroy());
    this._aoeRadiusHighlights = [];

    const combatStore = useCombatStore.getState();
    if (!tilePos) return;
    if (queueActive || combatStore.queueBusy) return;

    if (combatStore.inputMode === 'movement' && this.context.canPlayerMove()) {
      if (this.context.isValidMoveTarget(tilePos)) {
        this._hoverHighlight = this.createTileHighlight(tilePos.x, tilePos.y, 0xffff00, 0.4);
        this._hoverHighlight?.setDepth(DepthOrder.SELECTION_HIGHLIGHTS);
      }
    } else if (combatStore.inputMode === 'target-selection') {
      const abilityId = useCombatStore.getState().selectedAbilityId;
      const selectedAbility = abilityId ? (abilityService.getAbilityFromCache(abilityId) ?? null) : null;

      if (selectedAbility?.target.aoeRadius != null) {
        // AoE targeting: show center tile (yellow) + blast radius (orange) if in range
        const rangeTiles = this.context.getAbilityRangeTiles();
        const isInRange = rangeTiles.some(t => t.x === tilePos.x && t.y === tilePos.y);
        if (isInRange) {
          this._hoverHighlight = this.createTileHighlight(tilePos.x, tilePos.y, 0xffff00, 0.4);
          this._hoverHighlight?.setDepth(DepthOrder.SELECTION_HIGHLIGHTS);

          const radiusTiles = this.getAoeRadiusTiles(tilePos, selectedAbility.target.aoeRadius);
          for (const tile of radiusTiles) {
            if (tile.x === tilePos.x && tile.y === tilePos.y) continue;
            const h = this.createTileHighlight(tile.x, tile.y, 0xff8800, 0.25, 0, 0);
            if (h) {
              h.setDepth(DepthOrder.RANGE_HIGHLIGHTS);
              this._aoeRadiusHighlights.push(h);
            }
          }
        }
      } else {
        // Single-target: highlight entity at tile if valid
        const entityAtPos = this.context.combat.entityStates.find(
          (e: EntityCombatStateDTO) => e.position.x === tilePos.x && e.position.y === tilePos.y && e.status !== EntityStatus.DEAD
        );
        if (entityAtPos && this.context.isValidAbilityTarget(entityAtPos)) {
          this._hoverHighlight = this.createTileHighlight(tilePos.x, tilePos.y, 0xff0000, 0.4);
          this._hoverHighlight?.setDepth(DepthOrder.SELECTION_HIGHLIGHTS);
        }
      }
    }
  }

  private getAoeRadiusTiles(center: CombatPosition, radius: number): CombatPosition[] {
    const tiles: CombatPosition[] = [];
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        if (Math.abs(dx) + Math.abs(dy) <= radius) {
          tiles.push({ x: center.x + dx, y: center.y + dy });
        }
      }
    }
    return tiles;
  }

  private createTileHighlight(
    tileX: number,
    tileY: number,
    color: number,
    fillAlpha: number,
    strokeWidth = 2,
    strokeAlpha = 0.8,
  ): Phaser.GameObjects.Shape | null {
    const worldPos = this.coords.tileToWorld(tileX + 0.5, tileY + 0.5);

    const width = this.tileWidth;
    const height = this.tileHeight;
    const halfWidth = width * 0.5;
    const halfHeight = height * 0.5;
    const polygonPoints = [
      { x: halfWidth, y: 0 },
      { x: width, y: halfHeight },
      { x: halfWidth, y: height },
      { x: 0, y: halfHeight },
    ];

    const polygon = this.scene.add.polygon(worldPos.x, worldPos.y, polygonPoints, color, fillAlpha);
    polygon.setOrigin(0.5, 0.5);
    polygon.setStrokeStyle(strokeWidth, color, strokeAlpha);
    return polygon;
  }
}
